import React from 'react';
import { Pressable, ScrollView, StyleProp, ViewStyle } from 'react-native';
import styled from '@emotion/native';
import { H1 } from '@/shared/components/StyledText';
import {
  black,
  green,
  alphaBlack50,
  mintGreen,
  sand,
  white,
} from '@/shared/styles/colors';
import useDimensions from '@/shared/hooks/useDimensions';
import { useImageRatio } from '../hooks/useImageRatio';
import Button from './Button';
import useBreakpoint from '../hooks/useBreakpoint';
import { IsDesktopProps } from '../styles/types';
import PillTicker from './carousel/PillTicker';
import useSendbirdChannelUrl from '@/shared/hooks/useSendbirdChannelUrl';
import { useChannelRouting } from '@/shared/hooks/useChannelRouting';
import { useGetUser } from '@/shared/hooks/useGetUser';
import ViewPrivateNotesLink from './ViewPrivateNotesLink';
import { getAge } from '@/shared/utils/profileUtils';
import { getCityLabel } from '@/components/explore/filters/utils';
import { City, State } from '@/shared/types/generated';

interface ClientProfileProps {
  clientName: string;
  aesthetics: string[];
  profilePicUrl: string;
  bio?: string | null;
  sizingDetails?: string | null;
  height?: string | null;
  city?: City | null;
  state?: State | null;
  birthDate?: Date | string | null;
  shareAge?: boolean | null;
  clientUserId?: number;
  clientId?: number;
  showShadow?: boolean;
  onClose?: () => void;
  isModal?: boolean;
}

interface IsDesktopModalProps {
  isDesktopModal: boolean;
}

const desktopContainerWidth = 601;

const ClientProfile: React.FC<ClientProfileProps> = ({
  clientName,
  aesthetics,
  profilePicUrl,
  bio,
  sizingDetails,
  height,
  city,
  state,
  birthDate,
  shareAge,
  clientUserId,
  clientId,
  showShadow,
  onClose,
  isModal = false,
}) => {
  const { width } = useDimensions();
  const { isDesktop } = useBreakpoint();
  const isDesktopModal = isDesktop && isModal;
  const isMobileModal = !isDesktop && isModal;
  const { user } = useGetUser();
  const { fetchChannelUrl } = useSendbirdChannelUrl();
  const { navigateToChannel } = useChannelRouting();

  const profilePicRatio = useImageRatio(profilePicUrl);

  const age = getAge(birthDate);
  const locationText =
    city != null && state != null ? `${getCityLabel(city)}, ${state}` : null;

  const onPressGoToChat = async () => {
    if (!user?.id || !clientUserId) {
      return;
    }
    const channelUrl = await fetchChannelUrl(user.id, clientUserId);
    if (channelUrl) {
      navigateToChannel(channelUrl);
      onClose?.();
    }
  };

  const renderContent = () => (
    <>
      <BackgroundContainer isModal={isModal} isDesktop={isDesktop}>
        <MobilePreviewContainer isModal={isModal}>
          {onClose && (
            <CloseButton onPress={onClose}>
              <CloseIcon
                source={require('@/assets/images/CloseGreen.png')}
                tintColor={green}
              />
            </CloseButton>
          )}

          {(locationText || (shareAge && age !== null) || height) && (
            <IconsContainer>
              {locationText && (
                <IconItem>
                  <IconImage
                    source={require('@/assets/images/LocationIcon.png')}
                  />
                  <IconText>{locationText}</IconText>
                </IconItem>
              )}
              {shareAge && age !== null && (
                <IconItem>
                  <IconImage source={require('@/assets/images/CakeIcon.png')} />
                  <IconText>{age} Years</IconText>
                </IconItem>
              )}
              {height && (
                <IconItem>
                  <IconImage
                    source={require('@/assets/images/RulerIcon.png')}
                  />
                  <IconText>{height}</IconText>
                </IconItem>
              )}
            </IconsContainer>
          )}

          <StyledH1 isDesktopModal={isDesktopModal}>{clientName}</StyledH1>

          <PillTicker
            tags={aesthetics}
            maxWidth={isDesktop ? desktopContainerWidth : width}
            containerStyle={{ marginBottom: isDesktopModal ? 32 : 36 }}
          />

          <ProfileImageContainer isDesktopModal={isDesktopModal}>
            <ProfileImage
              isDesktopModal={isDesktopModal}
              source={{ uri: profilePicUrl }}
              style={{ aspectRatio: profilePicRatio }}
            />
          </ProfileImageContainer>

          {bio && (
            <BioContainer isDesktopModal={isDesktopModal}>
              <BioText>{bio}</BioText>
            </BioContainer>
          )}

          {sizingDetails && (
            <SizingContainer isDesktopModal={isDesktopModal}>
              <SizingLabel>SIZING</SizingLabel>
              <SizingText>{sizingDetails}</SizingText>
            </SizingContainer>
          )}

          {clientId && (
            <ViewPrivateNotesContainer>
              <ViewPrivateNotesLink clientId={clientId} onPress={onClose} />
            </ViewPrivateNotesContainer>
          )}
        </MobilePreviewContainer>
        {isDesktopModal && (
          <Button
            customStyle={{ marginBottom: 66, backgroundColor: white }}
            text="GO TO CHAT"
            variant="tertiary"
            onPress={onPressGoToChat}
          />
        )}
      </BackgroundContainer>
    </>
  );

  if (isDesktopModal) {
    return (
      <ModalContainer isDesktop={isDesktop}>
        <ScrollView
          style={{ maxHeight: 650, width: desktopContainerWidth }}
          contentContainerStyle={{
            alignItems: 'center',
          }}
          showsVerticalScrollIndicator={false}
        >
          {renderContent()}
        </ScrollView>
      </ModalContainer>
    );
  }

  if (isMobileModal) {
    return (
      <ModalContainer isDesktop={isDesktop}>
        <ScrollView
          style={{ flex: 1, width: '100%' }}
          contentContainerStyle={{
            alignItems: 'center',
            paddingVertical: 20,
          }}
          showsVerticalScrollIndicator={false}
        >
          {renderContent()}
        </ScrollView>
        {isMobileModal && (
          <MobileButtonContainer isDesktop={isDesktop}>
            <Button
              customStyle={{ backgroundColor: white }}
              text="GO TO CHAT"
              variant="tertiary"
              onPress={onPressGoToChat}
            />
          </MobileButtonContainer>
        )}
      </ModalContainer>
    );
  }

  return (
    <ScrollContainer
      contentContainerStyle={[
        scrollViewContentContainer,
        showShadow ? shadowStyle : {},
      ]}
    >
      {renderContent()}
    </ScrollContainer>
  );
};

export default ClientProfile;

const ScrollContainer = styled.ScrollView({
  flex: 1,
});

const ModalContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  backgroundColor: isDesktop ? alphaBlack50 : sand,
  justifyContent: isDesktop ? 'center' : 'flex-start',
  alignItems: 'center',
  width: !isDesktop ? '100%' : 'auto',
  height: !isDesktop ? '100%' : 'auto',
  paddingTop: !isDesktop ? 24 : 0,
}));

const scrollViewContentContainer: StyleProp<ViewStyle> = {
  justifyContent: 'center',
  alignItems: 'center',
};

const shadowStyle: ViewStyle = {
  shadowColor: black,
  shadowOffset: {
    width: 0,
    height: 5,
  },
  shadowRadius: 25,
  shadowOpacity: 1,
  elevation: 5,
};

const BackgroundContainer = styled.View(
  ({ isModal, isDesktop }: { isModal: boolean; isDesktop: boolean }) => ({
    alignItems: 'center',
    backgroundColor: isModal ? sand : green,
    borderRadius: 10,
    width: isDesktop ? desktopContainerWidth : '100%',
    paddingBottom: isModal && !isDesktop ? 100 : 0,
  }),
);

const MobilePreviewContainer = styled.View(
  ({ isModal }: { isModal: boolean }) => ({
    flexGrow: 1,
    width: '100%',
    alignItems: 'center',
    backgroundColor: sand,
    justifyContent: 'center',
    paddingTop: isModal ? 48 : 0,
  }),
);

// @ts-expect-error this is that strange error about lineHeight
const StyledH1 = styled(H1)<IsDesktopModalProps>(({ isDesktopModal }) => ({
  fontSize: isDesktopModal ? 34 : 50,
  textAlign: 'center',
  textTransform: 'uppercase',
  marginTop: 20,
  marginBottom: isDesktopModal ? 23 : 26,
  lineHeight: 'normal',
}));

const ProfileImageContainer = styled.View<IsDesktopModalProps>(
  ({ isDesktopModal }) => ({
    width: 296,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    marginHorizontal: 20,
    marginBottom: isDesktopModal ? 32 : 24,
  }),
);

const ProfileImage = styled.Image<IsDesktopModalProps>(
  ({ isDesktopModal }) => ({
    width: isDesktopModal ? 316 : '100%',
  }),
);

const CloseButton = styled(Pressable)({
  position: 'absolute',
  top: 20,
  right: 20,
  zIndex: 1,
});

const CloseIcon = styled.Image({
  width: 24,
  height: 24,
});

const MobileButtonContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  position: isDesktop ? 'relative' : 'absolute',
  bottom: 0,
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: isDesktop ? 'auto' : 113,
  backgroundColor: isDesktop ? 'transparent' : white,
  paddingHorizontal: 29,
}));

const ViewPrivateNotesContainer = styled.View({
  alignItems: 'center',
  marginBottom: 28,
});

const IconsContainer = styled.View({
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: 30,
  gap: 16,
});

const IconItem = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
});

const IconImage = styled.Image({
  width: 16,
  height: 16,
  resizeMode: 'contain',
});

const IconText = styled.Text({
  fontSize: 13,
  fontWeight: 'bold',
  color: black,
});

const BioContainer = styled.View<IsDesktopModalProps>(({ isDesktopModal }) => ({
  width: 296,
  marginHorizontal: 20,
  marginBottom: isDesktopModal ? 24 : 28,
  paddingHorizontal: 16,
}));

const BioText = styled.Text({
  fontSize: 18,
  fontWeight: '400',
  color: black,
  textAlign: 'center',
});

const SizingContainer = styled.View<IsDesktopModalProps>(
  ({ isDesktopModal }) => ({
    width: 296,
    marginHorizontal: 20,
    marginBottom: isDesktopModal ? 24 : 28,
    backgroundColor: mintGreen,
    borderRadius: 8,
    padding: 16,
  }),
);

const SizingLabel = styled.Text({
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: -0.12,
  color: black,
  marginBottom: 8,
});

const SizingText = styled.Text({
  fontSize: 12,
  color: black,
  letterSpacing: -0.12,
});
